// Common
import { Component, OnDestroy, effect, inject, input } from '@angular/core';

// Services
import { GoogleAnalyticsService } from '@frontendlib/common/services/google-analytics.service';
import { BoardService } from '@modules/tasks/services/board.service';

// RX
import { switchMap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

// Types
import { Column, ColumnFormGroup } from '@modules/tasks/types/column';
import { Section } from '@modules/common/types/section';

export type ColumnContextMenuOrientation = 'horizontal' | 'vertical';

@Component({
  selector: 'app-project-board-column-context-menu',
  templateUrl: './column-context-menu.component.html',
  styleUrls: ['./column-context-menu.component.less'],
  standalone: false,
})
export class ColumnContextMenuComponent implements OnDestroy {
  public readonly column = input.required<Column>();
  public readonly section = input<Section>();
  public readonly orientation = input<ColumnContextMenuOrientation>('horizontal');

  public columnFormGroup: ColumnFormGroup;
  private readonly colorChanges = new Subject<void>();
  private readonly alive = new Subject<void>();

  private readonly ga = inject(GoogleAnalyticsService);
  private readonly boardService = inject(BoardService);

  constructor() {
    this.colorChanges
      .pipe(
        switchMap(() => this.columnFormGroup.valueChanges),
        takeUntil(this.alive),
      )
      .subscribe(() => {
        this.boardService.updateColumn(Column.fromFormGroup(this.columnFormGroup));
      });

    effect(() => {
      const column = this.column();
      if (!column) return;
      this.columnFormGroup = column.asFormGroup();
      this.colorChanges.next();
    });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  delete() {
    const column = this.column();
    if (column.uncategorized) {
      return;
    }
    this.ga.trackEvent('column-context-menu', 'delete');
    this.boardService.removeColumn(column);
  }

  addLeft() {
    this.boardService.addColumn(false, this.column().position, this.section()?.id ?? null);
  }

  addRight() {
    this.boardService.addColumn(true, this.column().position, this.section()?.id ?? null);
  }

  moveLeft() {
    this.boardService.moveColumn(this.column(), -1);
  }

  moveRight() {
    this.boardService.moveColumn(this.column(), 1);
  }
}
